import type { FC } from "react";

import { type ReasoningEffort } from "./blackboardModels";

interface ReasoningEffortChipProps {
  /** Resolved effort the runtime uses (override > template > model default). */
  reasoningEffort: ReasoningEffort | null;
  /** Raw instance override, if any — present only when source === "instance". */
  override?: ReasoningEffort | null;
  /** Where the resolved value came from. */
  source?: "instance" | "template" | null;
  /** Card-dense variant: drops the "EFFORT" prefix, keeps level + intensity. */
  compact?: boolean;
}

// Effort is a cost/depth dial, so the ramp runs cool → hot. Filled segments
// (out of 5) give an at-a-glance intensity read without reading the word.
const EFFORT_META: Record<
  ReasoningEffort,
  { label: string; bars: number; tone: string }
> = {
  high: { bars: 4, label: "HIGH", tone: "text-amber-600 dark:text-amber-300" },
  low: { bars: 2, label: "LOW", tone: "text-sky-600 dark:text-sky-300" },
  max: { bars: 5, label: "MAX", tone: "text-rose-600 dark:text-rose-300" },
  medium: {
    bars: 3,
    label: "MED",
    tone: "text-emerald-600 dark:text-emerald-300",
  },
  minimal: {
    bars: 1,
    label: "MIN",
    tone: "text-slate-500 dark:text-slate-400",
  },
  none: { bars: 0, label: "NONE", tone: "text-slate-500 dark:text-slate-500" },
  xhigh: { bars: 5, label: "XHI", tone: "text-rose-600 dark:text-rose-300" },
};

/**
 * Bordered pill surfacing an actor's reasoning effort — the dial that was
 * configurable (template + instance override) but never shown in the fleet/
 * drawer chrome. Renders nothing when effort is unset (model doesn't expose
 * the dial, or never configured) so it only appears where it's meaningful.
 *
 * Intensity reads two ways: the level word (LOW/MED/HIGH/…) and a 5-segment
 * cool→hot bar. An instance override is marked with a trailing `*` + a solid
 * (vs dashed) border, mirroring ModelChip's Bedrock-routing cue, so operators
 * can tell a per-actor tweak from an inherited default at a glance.
 */
export const ReasoningEffortChip: FC<ReasoningEffortChipProps> = ({
  reasoningEffort,
  override,
  source,
  compact = false,
}) => {
  if (!reasoningEffort) {
    return null;
  }
  const meta = EFFORT_META[reasoningEffort];
  const isOverride = source === "instance" && override != null;
  const borderStyle = isOverride ? "" : "border-dashed";
  let sourceSuffix = "";
  if (isOverride) {
    sourceSuffix = " (instance override)";
  } else if (source) {
    sourceSuffix = ` (${source} default)`;
  }
  return (
    <span
      className={`inline-flex items-center gap-1 border ${borderStyle} border-slate-300 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest dark:border-slate-700/60 ${meta.tone}`}
      title={`Reasoning effort: ${reasoningEffort}${sourceSuffix}`}
    >
      {!compact && (
        <span className="text-slate-400 dark:text-slate-600">EFFORT</span>
      )}
      <span>{meta.label}</span>
      <span aria-hidden className="flex items-center gap-px">
        {Array.from({ length: 5 }, (_, i) => (
          <span
            key={i}
            className={`h-2 w-0.5 ${
              i < meta.bars ? "bg-current" : "bg-current opacity-20"
            }`}
          />
        ))}
      </span>
      {isOverride && <span aria-hidden>*</span>}
    </span>
  );
};
